import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Cache, actions } from '@kanda-libs/ks-frontend-services';
import { ExtractedError } from '@kanda-libs/ks-frontend-services/src/types';

import { useAppDispatch } from 'components/App';
import { selectors } from 'store';

export type ExtendedCache = Record<string, string | undefined> & Cache;

export interface GetCacheHook {
  cache: ExtendedCache | undefined;
  isValidating: boolean;
  isLoading: boolean;
  getCache: () => void;
  error: ExtractedError | undefined;
}

export default function useGetCache(): GetCacheHook {
  const dispatch = useAppDispatch();
  const caches = useSelector(selectors.infoGetCache.getEntities) as
    | Record<string, ExtendedCache>
    | undefined;
  // const { ids } = useSelector(selectors.infoGetCache.getReducer);
  const error = useSelector(selectors.infoGetCache.getError);
  const isSubmitting = useSelector(selectors.infoGetCache.getIsSubmitting);
  const isLoading = useSelector(selectors.infoGetCache.getIsLoading);
  const hasFetched = useSelector(selectors.infoGetCache.getHasFetched);

  const cache = useMemo(() => {
    if (isLoading || !hasFetched || !caches) return undefined;
    const id = Object.keys(caches)?.[0];
    return id ? caches[id] : undefined;
  }, [isLoading, hasFetched, caches]);

  const getCache = useCallback(
    () => dispatch(actions.infoGetCache({})),
    [dispatch],
  );

  useEffect(() => {
    if (hasFetched) return;
    getCache();
  }, [hasFetched, getCache]);

  return {
    cache,
    isValidating: isSubmitting,
    isLoading,
    getCache,
    error,
  };
}
